import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "codeStep",
    "codeForm",
    "emailStep",
    "accountStep",
    "loginStep",
    "codeInput",
    "emailInput",
    "organizationName",
    "organizationFoundMessage",
    "registrationEmailInput",
    "loginEmailInput",
    "welcomeName",
    "errorMessage"
  ]

  static values = {
    validateUrl: String,
    checkUserUrl: String
  }

  connect() {
    this.codeValidated = false
    this.currentContextToken = ''
    this.showCodeStep()
  }

  showCodeStep() {
    this.codeValidated = false
    this.currentContextToken = ''
    this.syncContextTokenInputs()
    this.codeStepTarget.classList.remove('hidden')
    this.codeFormTarget.classList.remove('hidden')
    this.emailStepTarget.classList.add('hidden')
    this.emailStepTarget.classList.add('opacity-0', '-translate-y-2')
    this.accountStepTarget.classList.add('hidden')
    this.loginStepTarget.classList.add('hidden')
  }

  showAccountStep() {
    this.codeStepTarget.classList.add('hidden')
    this.emailStepTarget.classList.add('hidden')
    this.accountStepTarget.classList.remove('hidden')
    this.loginStepTarget.classList.add('hidden')
  }

  showLoginStep() {
    this.codeStepTarget.classList.add('hidden')
    this.emailStepTarget.classList.add('hidden')
    this.accountStepTarget.classList.add('hidden')
    this.loginStepTarget.classList.remove('hidden')
  }

  showEmailStep() {
    this.codeStepTarget.classList.add('hidden')
    this.accountStepTarget.classList.add('hidden')
    this.loginStepTarget.classList.add('hidden')
    this.emailStepTarget.classList.remove('hidden')
    requestAnimationFrame(() => {
      this.emailStepTarget.classList.remove('opacity-0', '-translate-y-2')
    })
  }

  async validateCode(event) {
    event.preventDefault()

    const code = this.codeInputTarget.value.trim()
    if (!code) {
      this.showError(this.element.dataset.codeRequiredMessage || "Please enter a code")
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const locale = this.element.dataset.locale || 'en'
    const validateUrl = this.validateUrlValue || '/organization_codes/validate'

    try {
      const response = await fetch(validateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          organization_code: { code: code },
          locale: locale
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        this.showError(errorData.error || this.element.dataset.invalidCodeMessage || "Invalid code")
        return
      }

      const data = await response.json()

      if (data.valid) {
        this.hideError()
        this.codeValidated = true
        this.setOrganizationName(data.organization_name)
        this.setContextToken(data.context_token)
        this.showEmailStep()
      } else {
        this.showError(data.error || this.element.dataset.invalidCodeMessage || "Invalid code")
      }
    } catch (error) {
      this.showError(this.element.dataset.errorMessage || "An error occurred. Please try again.")
    }
  }

  async checkUserExistence(event) {
    event.preventDefault()

    if (!this.codeValidated) {
      this.showError(this.element.dataset.invalidCodeMessage || "Invalid code")
      return
    }

    const email = this.emailInputTarget.value.trim().toLowerCase()

    if (!email) {
      this.showError(this.element.dataset.emailRequiredMessage || "Please enter an email address")
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const locale = this.element.dataset.locale || 'en'
    const checkUserUrl = this.checkUserUrlValue || '/organization_codes/check_user_existence'

    try {
      const response = await fetch(checkUserUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          user: { email_address: email },
          context_token: this.currentContextToken,
          locale: locale
        })
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        this.showError(data.error || this.element.dataset.errorMessage || "An error occurred. Please try again.")
        return
      }

      this.hideError()
      this.setOrganizationName(data.organization_name)
      this.setContextToken(data.context_token)

      const normalizedEmail = data.email || email
      this.registrationEmailInputTarget.value = normalizedEmail
      this.loginEmailInputTarget.value = normalizedEmail

      if (data.exists) {
        this.welcomeNameTarget.textContent = data.name || normalizedEmail
        this.showLoginStep()
      } else {
        this.showAccountStep()
      }
    } catch (_error) {
      this.showError(this.element.dataset.errorMessage || "An error occurred. Please try again.")
    }
  }

  showError(message) {
    this.errorMessageTargets.forEach((target) => {
      target.textContent = message
      target.classList.remove('hidden')
    })
  }

  hideError() {
    this.errorMessageTargets.forEach((target) => {
      target.textContent = ''
      target.classList.add('hidden')
    })
  }

  goBack(event) {
    event.preventDefault()
    this.showCodeStep()
  }

  goBackToEmail(event) {
    event.preventDefault()
    this.showEmailStep()
  }

  setOrganizationName(organizationName) {
    this.organizationNameTargets.forEach((target) => {
      target.textContent = organizationName
    })
  }

  setContextToken(contextToken) {
    this.currentContextToken = contextToken || ''
    this.syncContextTokenInputs()
  }

  syncContextTokenInputs() {
    this.element.querySelectorAll('input[name="context_token"]').forEach((input) => {
      input.value = this.currentContextToken
    })
  }
}
